import { useState, useRef } from "react";
import { api, publicApi, formatTL } from "@/lib/api";
import { printReceipt } from "@/lib/receipt";
import { X, Minus, Plus, ShoppingBag, CheckCircle2, CreditCard, Banknote, Printer, Search, Tag } from "lucide-react";
import { toast } from "sonner";
import { useT, buildNote } from "@/lib/kioskI18n";

const PAYMENTS = [
  { id: "Teslimde Kredi Kartı", label: "Teslimde Kredi Kartı", icon: CreditCard },
  { id: "Teslimde Nakit Ödeme", label: "Teslimde Nakit Ödeme", icon: Banknote },
];
const LINK_PAYMENTS = [
  { id: "Kredi Kartı", label: "Kredi Kartı", icon: CreditCard },
  { id: "Teslimde Nakit Ödeme", label: "Teslimde Nakit Ödeme", icon: Banknote },
];

export const CartDrawer = ({
  open,
  onClose,
  cart,
  updateQty,
  updateNote,
  subtotal,
  tax,
  total,
  placeOrder,
  clearCart,
  orderType,
  onFinished,
  discount,
  discountAmount = 0,
  grossTotal,
  onApplyCode,
  onRemoveCode,
}) => {
  const { t, m, lang } = useT();
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const submitCode = async () => {
    if (!codeInput.trim()) return;
    setCodeLoading(true);
    setCodeError("");
    try {
      await onApplyCode(codeInput.trim());
      setCodeInput("");
    } catch (e) {
      setCodeError(t(e?.response?.data?.detail || "İndirim kodu geçersiz"));
      setCodeInput("");
    } finally {
      setCodeLoading(false);
    }
  };
  const [checkout, setCheckout] = useState(false);
  const isLink = orderType === "Link";
  const [payment, setPayment] = useState("Teslimde Nakit Ödeme");
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const finishTimer = useRef(null);
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [lookingUp, setLookingUp] = useState(false);
  const isDelivery = orderType === "Paket Servis";
  const paymentOptions = isLink ? LINK_PAYMENTS : PAYMENTS;

  const close = () => {
    clearTimeout(finishTimer.current);
    onClose();
    setTimeout(() => {
      setCheckout(false);
      setConfirmed(null);
      setCustomer({ name: "", phone: "", address: "" });
      setCodeInput("");
      setCodeError("");
    }, 300);
  };

  const lookupCustomer = async () => {
    const phone = customer.phone.trim();
    if (!phone) return;
    setLookingUp(true);
    try {
      const res = await api.get(`/customers/lookup`, { params: { phone } });
      if (res.data.found) {
        const c = res.data.customer;
        setCustomer({ name: c.name, phone: c.phone, address: c.address });
        toast.success("Kayıtlı müşteri bulundu, bilgiler dolduruldu");
      }
    } catch (e) {
      /* sessiz geç */
    }
    setLookingUp(false);
  };

  const handlePlace = async () => {
    if (isDelivery && (!customer.name.trim() || !customer.phone.trim() || !customer.address.trim())) {
      toast.error("Paket servis için ad, telefon ve adres zorunludur");
      return;
    }
    if (isLink && (!customer.name.trim() || !customer.phone.trim())) {
      toast.error(t("Lütfen ad soyad ve telefon numaranızı girin"));
      return;
    }
    if ((isDelivery || isLink) && customer.phone.replace(/\D/g, "").length !== 10) {
      toast.error(t("Telefon numarası 10 haneli olmalıdır"));
      return;
    }
    setPlacing(true);
    try {
      const order = await placeOrder(payment, isDelivery || isLink ? customer : null);
      // Link + Kredi Kartı: önce iyzico'ya yönlendir. Ödeme onaylanmadan sipariş ONAYLANMAZ.
      if (isLink && payment === "Kredi Kartı") {
        try {
          const res = await publicApi.post("/payments/iyzico/initialize", {
            order_id: order.id,
            origin: window.location.origin,
          });
          clearCart?.();
          setConfirmed(order);
          setPlacing(false);
          // Nakitteki gibi sipariş numarası ekranı gösterilsin, sonra iyzico ödeme sayfasına yönlendir
          setTimeout(() => {
            window.location.assign(res.data.paymentPageUrl);
          }, 3500);
          return;
        } catch (err) {
          // Ödeme başlatılamadı → siparişi ONAYLAMA, bekleyen (ödenmemiş) kaydı sil, sepeti koru
          try { await publicApi.post(`/orders/${order.id}/cancel-unpaid`); } catch (_) {}
          const detail = err?.response?.data?.detail;
          if (err?.response?.status === 503) {
            toast.error("Bu şube için kart ödemesi ayarlanmamış. Lütfen 'Teslimde Nakit Ödeme' seçin.");
          } else {
            toast.error(detail ? `Kart ödemesi başlatılamadı: ${detail}` : "Kart ödemesi başlatılamadı. Lütfen tekrar deneyin veya nakit seçin.");
          }
          setPlacing(false);
          return;
        }
      }
      clearCart?.();
      setConfirmed(order);
      // Link (online web) siparişlerinde müşteri cihazında yazdırma yok; şube panosu mutfak fişini otomatik basar
      if (!isLink) {
        try {
          printReceipt(order, "customer"); // kiosk: sadece müşteri fişi (mutfak fişi sipariş panosundan)
        } catch (_) {
          toast.error(t("Sipariş alındı ama fiş yazdırılamadı"));
        }
      }
      clearTimeout(finishTimer.current);
      finishTimer.current = setTimeout(() => {
        close();
        onFinished?.(); // sipariş sonrası ana (karşılama) ekranına dön
      }, 5000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const fail = t("Sipariş oluşturulamadı");
      toast.error(
        typeof detail === "string"
          ? `${fail}: ${t(detail)}`
          : !e?.response
          ? `${fail}: ${t("internet bağlantısı yok veya sunucuya ulaşılamıyor")}`
          : `${fail} (${t("hata")} ${e.response.status})`
      );
    }
    setPlacing(false);
  };

  return (
    <>
      {/* overlay */}
      <div
        onClick={close}
        className={`fixed inset-0 z-[60] bg-black/40 transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* panel */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[70] h-full w-full sm:w-[440px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="flex items-center justify-between px-5 h-16 border-b border-[var(--fb-border)] shrink-0">
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[var(--fb-red)]" />
            {confirmed ? t("Sipariş Alındı") : checkout ? t("Ödeme") : t("Sepetim")}
          </h2>
          <button
            data-testid="close-cart-btn"
            onClick={close}
            className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {confirmed && confirmed.order_number ? (
          <div
            data-testid="order-confirm-modal"
            className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4"
          >
            <CheckCircle2 className="w-20 h-20 text-[var(--fb-success,#16A34A)] text-green-600 animate-pop" />
            <h3 className="font-display font-extrabold text-2xl">{t("Siparişiniz Alındı!")}</h3>
            <p className="text-[var(--fb-muted)]">{t("Sipariş numaranız")}</p>
            <div
              data-testid="order-number"
              className="font-display font-extrabold text-5xl text-[var(--fb-red)]"
            >
              #{confirmed.order_number}
            </div>
            <p className="text-sm text-[var(--fb-muted)]">
              {t(confirmed.order_type)} · {t(confirmed.payment_method)}
            </p>
            <button
              data-testid="print-customer-btn"
              onClick={() => printReceipt(confirmed, "customer")}
              className="w-full mt-2 flex items-center justify-center gap-2 bg-[var(--fb-ink)] hover:bg-black text-white font-semibold px-4 py-2.5 rounded-full transition-colors text-sm"
            >
              <Printer className="w-4 h-4" /> {t("Fişi Tekrar Yazdır")}
            </button>
            <p className="text-xs text-[var(--fb-muted)] mt-1">
              {t("Fişiniz yazdırılıyor · 5 saniye içinde ana ekrana dönülecek")}
            </p>
            <button
              data-testid="new-order-btn"
              onClick={() => {
                clearTimeout(finishTimer.current);
                close();
                onFinished?.();
              }}
              className="mt-2 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white font-bold px-6 py-3 rounded-full transition-colors"
            >
              {t("Yeni Sipariş")}
            </button>
          </div>
        ) : cart.length === 0 ? (
          <div className="flex-1 grid place-items-center text-[var(--fb-muted)] px-8 text-center">
            {t("Sepetiniz boş. Menüden ürün ekleyin.")}
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto fb-scroll px-5 py-4 space-y-4">
              {cart.map((item) => (
                <div
                  key={item.uid}
                  data-testid={`cart-item-${item.product_id}`}
                  className="bg-[var(--fb-offwhite)] rounded-xl p-3 border border-[var(--fb-border)]"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{m(item.name)}</p>
                      <p className="text-sm text-[var(--fb-red)] font-bold">
                        {formatTL(item.price)}
                      </p>
                      {item.note && (
                        <p className="text-xs text-[var(--fb-red)] font-medium mt-0.5">{lang === "en" && item.opts ? buildNote(item.opts, t, m) : item.note}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 bg-white rounded-full border border-[var(--fb-border)] p-0.5">
                      <button
                        data-testid={`qty-dec-${item.product_id}`}
                        onClick={() => updateQty(item.uid, -1)}
                        className="w-7 h-7 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-6 text-center font-bold text-sm">
                        {item.quantity}
                      </span>
                      <button
                        data-testid={`qty-inc-${item.product_id}`}
                        onClick={() => updateQty(item.uid, 1)}
                        className="w-7 h-7 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {!checkout && (
                    <input
                      data-testid={`cart-note-${item.product_id}`}
                      value={item.note}
                      onChange={(e) => updateNote(item.uid, e.target.value)}
                      placeholder={t("Not ekle (örn. soğansız)")}
                      className="mt-2 w-full text-sm bg-white border border-[var(--fb-border)] rounded-lg px-3 py-1.5 outline-none focus:border-[var(--fb-red)]"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="border-t border-[var(--fb-border)] px-5 py-4 space-y-3 shrink-0">
              {checkout && isDelivery && (
                <div data-testid="delivery-customer-form" className="space-y-2 mb-1 bg-red-50 border border-red-100 rounded-xl p-3">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-red)]">
                    Paket Servis Bilgileri
                  </p>
                  <div className="relative">
                    <input
                      data-testid="customer-phone"
                      value={customer.phone}
                      inputMode="numeric"
                      maxLength={10}
                      onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                      onBlur={lookupCustomer}
                      placeholder="Telefon (10 haneli)"
                      className="w-full text-sm bg-white border border-[var(--fb-border)] rounded-lg pl-3 pr-9 py-2 outline-none focus:border-[var(--fb-red)]"
                    />
                    <button
                      type="button"
                      data-testid="customer-lookup-btn"
                      onClick={lookupCustomer}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-full text-[var(--fb-muted)] hover:text-[var(--fb-red)]"
                    >
                      <Search className={`w-4 h-4 ${lookingUp ? "animate-pulse" : ""}`} />
                    </button>
                  </div>
                  <input
                    data-testid="customer-name"
                    value={customer.name}
                    onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                    placeholder="Ad Soyad"
                    className="w-full text-sm bg-white border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
                  />
                  <textarea
                    data-testid="customer-address"
                    value={customer.address}
                    onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))}
                    placeholder="Teslimat adresi"
                    rows={2}
                    className="w-full text-sm bg-white border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)] resize-none"
                  />
                </div>
              )}
              {checkout && isLink && (
                <div data-testid="online-customer-form" className="space-y-2 mb-1 bg-black border border-black rounded-xl p-3">
                  <p className="text-xs uppercase tracking-wider font-semibold text-white">
                    {t("İletişim Bilgisi")}
                  </p>
                  <input
                    data-testid="online-customer-phone"
                    value={customer.phone}
                    inputMode="numeric"
                    maxLength={10}
                    onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                    placeholder={t("Telefon (10 haneli, zorunlu)")}
                    className="w-full text-sm bg-white border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
                  />
                  <input
                    data-testid="online-customer-name"
                    value={customer.name}
                    onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                    placeholder={t("Ad Soyad (zorunlu)")}
                    className="w-full text-sm bg-white border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
                  />
                </div>
              )}
              {checkout && (
                <div className={`grid ${paymentOptions.length === 1 ? "grid-cols-1" : paymentOptions.length === 2 ? "grid-cols-2" : "grid-cols-3"} gap-2 mb-2`}>
                  {paymentOptions.map((p) => {
                    const Icon = p.icon;
                    return (
                      <button
                        key={p.id}
                        data-testid={`payment-${p.id}`}
                        onClick={() => setPayment(p.id)}
                        className={`flex flex-col items-center gap-1 py-3 rounded-xl border text-xs font-semibold transition-colors ${
                          payment === p.id
                            ? "border-[var(--fb-red)] bg-red-50 text-[var(--fb-red)]"
                            : "border-[var(--fb-border)] text-[var(--fb-muted)]"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {t(p.label)}
                      </button>
                    );
                  })}
                </div>
              )}
              {checkout && onApplyCode && (
                <div data-testid="discount-code-box" className="mb-3">
                  {discount ? (
                    <div className="flex items-center justify-between gap-2 rounded-xl border-2 border-green-600 bg-green-50 px-3 py-2.5">
                      <span data-testid="discount-applied" className="flex items-center gap-2 text-sm font-bold text-green-800">
                        <Tag className="w-4 h-4" /> {discount.code} · {discount.type === "percent" ? `%${discount.value}` : formatTL(discount.value)} {t("indirim")}
                      </span>
                      <button data-testid="discount-remove-btn" onClick={onRemoveCode} className="text-xs font-bold text-[var(--fb-muted)] hover:text-[var(--fb-red)]">
                        {t("Kaldır")}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <input
                          data-testid="discount-code-input"
                          value={codeInput}
                          onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setCodeError(""); }}
                          onKeyDown={(e) => e.key === "Enter" && submitCode()}
                          placeholder={t("İndirim kodu")}
                          className="flex-1 min-w-0 text-sm font-bold uppercase bg-white border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
                        />
                        <button
                          data-testid="discount-apply-btn"
                          onClick={submitCode}
                          disabled={codeLoading || !codeInput.trim()}
                          className="shrink-0 bg-[var(--fb-ink)] disabled:opacity-40 text-white text-sm font-bold px-5 rounded-xl"
                        >
                          {codeLoading ? "..." : t("Uygula")}
                        </button>
                      </div>
                      {codeError && <p data-testid="discount-error" className="text-xs font-semibold text-[var(--fb-red)] mt-1">{codeError}</p>}
                    </>
                  )}
                </div>
              )}
              <div className="space-y-1 text-sm">
                {discountAmount > 0 && (
                  <>
                    <div className="flex justify-between text-[var(--fb-muted)]">
                      <span>{t("Sepet Tutarı")}</span>
                      <span>{formatTL(grossTotal)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-green-700">
                      <span>{t("İndirim")} ({discount?.code})</span>
                      <span data-testid="discount-amount">-{formatTL(discountAmount)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-[var(--fb-muted)]">
                  <span>{t("Ara Toplam (KDV hariç)")}</span>
                  <span>{formatTL(subtotal)}</span>
                </div>
                <div className="flex justify-between text-[var(--fb-muted)]">
                  <span>{t("KDV (%10) dahil")}</span>
                  <span>{formatTL(tax)}</span>
                </div>
                <div className="flex justify-between font-display font-extrabold text-lg pt-1">
                  <span>{t("Toplam (KDV dahil)")}</span>
                  <span data-testid="cart-total" className="text-[var(--fb-red)]">
                    {formatTL(total)}
                  </span>
                </div>
              </div>

              {checkout ? (
                <button
                  data-testid="place-order-btn"
                  disabled={placing}
                  onClick={handlePlace}
                  className="w-full bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold py-4 rounded-full transition-colors active:scale-[0.99]"
                >
                  {placing ? t("Gönderiliyor...") : `${t("Siparişi Onayla")} · ${formatTL(total)}`}
                </button>
              ) : (
                <button
                  data-testid="cart-checkout-btn"
                  onClick={() => setCheckout(true)}
                  className="w-full bg-[var(--fb-ink)] hover:bg-black text-white font-bold py-4 rounded-full transition-colors active:scale-[0.99]"
                >
                  {t("Siparişi Tamamla")}
                </button>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
};
